import React, { useState } from 'react';
import { useTracking } from 'react-tracking';
import {
  notification, NotificationConfig, NotificationType,
} from '@routific/hive';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import usePermissions, { Features } from '../../../../permissions/usePermissions';
import ComponentNames from '../../../../permissions/constants/componentNames';
import { useStore } from '../../../../store/mst';
import AnalyticsEvents from '../../../../analytics/analyticsEvents';
import { logglyLogger } from '../../../../services/logglyLogger';
import ActionButton, { ButtonTypes, CommonActionTypes } from '../../../shared/ActionButton';
import UnpublishIcon from '../../../shared/Icon/UnpublishIcon';
import UnpublishConfirmModal from './UnpublishConfirmModal';

const UnpublishButton: React.FC = () => {
  const { t } = useTranslation('components');
  const { trackEvent } = useTracking();
  const store = useStore();
  const { ui, model } = store;
  const route = ui.selectedRoute;
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  const {
    canDispatchRoutes,
  } = usePermissions(Features.Routes, ComponentNames.UnpublishButton, { isOptimizing: ui.isOptimizing });

  const isPublished = !!route?.isDispatched;
  const hasDriverProgress = !!route?.isStarted || (route?.completedStopsCount || 0) > 0;
  const disabled = !isPublished || hasDriverProgress || !canDispatchRoutes || ui.isPublishing || ui.isUnpublishing;

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
  };

  const unpublishFailureNotification = (): NotificationConfig => ({
    id: 'unpublish-routes-failure',
    type: NotificationType.ERROR,
    message: t('unpublish.button.error.text'),
    dataCy: 'unpublish-failure-notification',
  });

  const handleUnpublish = async () => {
    if (!route?.uuid) {
      return;
    }

    try {
      await model.unpublishSelectedRoutes([route.uuid]);
      ui.setIsUnpublishing(true);
      trackEvent({ event: AnalyticsEvents.UNPUBLISH_ROUTE });
    } catch (err) {
      logglyLogger.log({ error: err?.response?.errors?.[0]?.message ?? err?.message });
      notification(unpublishFailureNotification());
    }

    closeConfirmModal();
  };

  const openConfirmModal = () => {
    if (disabled) {
      return;
    }
    setShowConfirmModal(true);
  };

  const getTooltipText = (): string | undefined => {
    if (ui.isOptimizing) {
      return t('unpublish.tooltip.scheduling');
    }
    if (!isPublished) {
      return t('unpublish.tooltip.notPublished');
    }
    if (hasDriverProgress) {
      return t('unpublish.tooltip.routeInProgress');
    }
    return undefined;
  };

  return (
    <>
      <UnpublishConfirmModal
        visible={showConfirmModal}
        onOk={handleUnpublish}
        onCancel={closeConfirmModal}
      />
      <ActionButton
        name={CommonActionTypes.UNPUBLISH}
        tooltipProps={{
          title: getTooltipText(),
        }}
        type={ButtonTypes.MENU}
        disabled={disabled}
        loading={ui.isUnpublishing}
        menuItemProps={{
          onClick: openConfirmModal,
          icon: <UnpublishIcon />,
        }}
        menuItemText={t('moreActionsButton.route.button.unpublish.text')}
        buttonProps={{
          onClick: openConfirmModal,
        }}
      />
    </>
  );
};

export default observer(UnpublishButton);
